import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';

import { QueryParams, queryParams } from '@/app/constants/queryParams';
import { episodesDetailsQueryOptions } from '@/app/queries/episodes/episodesDetails';
import { sqids } from '@/app/utils/hashids/sqids-generator';
import { AudioProvider } from '@/common/contexts/audio-context';
import { useGetScriptsQuery } from '@/modules/scripts/queries/use-get-scripts-query';
import { AudioPreview } from '@/modules/studio/components/audio-preview';
import { MusicSelected } from '@/modules/studio/components/music-selected';
import { PublishBar } from '@/modules/studio/components/publish-bar';
import { ScriptBox } from '@/modules/studio/components/script-box';
import { ScriptHeader } from '@/modules/studio/components/script-header';
import { SidebarTools } from '@/modules/studio/components/sidebar-tools';
import { ToolPanel } from '@/modules/studio/components/tool-panel';
import { StudioStoreProvider } from '@/modules/studio/providers/studio-provider';

const StudioPodcast = () => {
  const { id: studioIds } = Route.useParams();
  const { episodeId, podcastId, projectId } = sqids.hydrate<QueryParams.Studio>(
    studioIds,
    queryParams.studio,
  );

  const { data: details } = useSuspenseQuery(
    episodesDetailsQueryOptions(projectId, podcastId, episodeId),
  );

  const { data: scripts = [], isLoading } = useGetScriptsQuery(projectId, podcastId, episodeId);

  return (
    <StudioStoreProvider>
      <AudioProvider>
        <div className='flex w-full h-full overflow-hidden'>
          <div className='flex flex-col h-full w-full relative'>
            <PublishBar
              name={details.podcastName}
              projectId={projectId}
              podcastId={podcastId}
              episodeId={episodeId}
              studioIds={studioIds}
            />
            <div className='flex flex-1 overflow-hidden w-full'>
              <ScriptHeader
                isLoading={isLoading}
                scripts={scripts}
                title={details.title}
                projectId={projectId}
                podcastId={podcastId}
                episodeId={episodeId}
              >
                <MusicSelected music={details.musicInUse} />
                <ScriptBox projectId={projectId} podcastId={podcastId} episodeId={episodeId} />
              </ScriptHeader>

              <ToolPanel />
              <SidebarTools />
            </div>
            <AudioPreview projectId={projectId} podcastId={podcastId} episodeId={episodeId} />
          </div>
        </div>
      </AudioProvider>
    </StudioStoreProvider>
  );
};

export const Route = createFileRoute('/_app/studio/$id/')({
  component: StudioPodcast,
});
